'use client';

import React from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UniversalModal from '@/components/modal/UniversalModal';
import { RESUME_EVALUATION_GROUP, TRACK_NAME } from '@/constants/site-track';
import { sendEvent } from '@/utils/site-track';

const EXIT_SURVEY_OPTIONS = [
  { value: 'not_job_seeking', label: '我不在找工作状态' },
  { value: 'just_browsing', label: '我只是想了解公司/职位信息，不需要诊断' },
  { value: 'no_resume', label: '我现在没有简历文件' },
  { value: 'flow_confusing', label: '我觉得步骤太多' },
  { value: 'not_sure_help', label: '我不肯定诊断对我有什么帮助' },
  { value: 'other', label: '其他' },
];

const STORAGE_KEY = 'resume_evaluation_exit_survey_done';

const ExitSurveyModal = () => {
  const [open, setOpen] = React.useState(false);
  const [thanksOpen, setThanksOpen] = React.useState(false);
  const [selectedReasons, setSelectedReasons] = React.useState<string[]>([]);
  const [otherReason, setOtherReason] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [thanksCountdown, setThanksCountdown] = React.useState(2);
  const pendingNavRef = React.useRef<string>('');

  React.useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY) === '1';
    if (!seen) {
      const handleLinkClick = (event: MouseEvent) => {
        if (
          event.defaultPrevented ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey
        ) {
          return;
        }
        const target = event.target as HTMLElement | null;
        const anchor = target?.closest('a[href]');
        if (!(anchor instanceof HTMLAnchorElement)) return;
        if (anchor.target && anchor.target !== '_self') return;
        const href = anchor.href;
        if (!href || href === window.location.href || href === '#') return;
        event.preventDefault();
        event.stopPropagation();
        setNavigationUrl(href);
      };

      window.addEventListener('click', handleLinkClick, true);
      return () => {
        window.removeEventListener('click', handleLinkClick, true);
      };
    }
  }, []);

  const markSeen = () => localStorage.setItem(STORAGE_KEY, '1');

  const toggleReason = (value: string) => {
    setSelectedReasons((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const setNavigationUrl = (navUrl: string) => {
    pendingNavRef.current = navUrl;
    setOpen(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const reasons = selectedReasons.length
      ? selectedReasons.join(',')
      : 'empty';
    const other = otherReason.trim();
    sendEvent({
      name: TRACK_NAME.evaluation_exit_survey_submit,
      ele_id: 'exit_survey_modal',
      ele_group: RESUME_EVALUATION_GROUP,
      reasons,
      other,
    });
    markSeen();
    setOpen(false);
    setThanksOpen(true);
    setIsSubmitting(false);
  };

  const proceedNavigation = React.useCallback(() => {
    const nav = pendingNavRef.current;
    pendingNavRef.current = '';
    if (nav) {
      window.location.href = nav;
    }
    setThanksOpen(false);
  }, []);

  const handleSkip = () => {
    markSeen();
    setOpen(false);
    proceedNavigation();
  };

  React.useEffect(() => {
    if (!thanksOpen) return;
    setThanksCountdown(2);
    const interval = window.setInterval(() => {
      setThanksCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(interval);
          proceedNavigation();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [thanksOpen, proceedNavigation]);

  return (
    <>
      <UniversalModal
        open={open}
        title=""
        headerHeight={0}
        headerClass="hidden"
        width={448}
        maxWidth={false}
        fullScreen={false}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-white">
            <Button
              variant="outlined"
              className="text-sm font-semibold text-web-black-100 h-10 px-4 !rounded-xl !border-slate-200"
              onClick={handleSkip}
            >
              直接退出
            </Button>
            <Button
              variant="contained"
              className="!text-sm !font-semibold !h-10 !px-4.5 !rounded-xl !bg-web-blue-500"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              提交
            </Button>
          </div>
        }
        data-ele-group={RESUME_EVALUATION_GROUP}
        data-ele-id="modal_离开调查"
      >
        <div className="px-9 py-6 space-y-5 bg-white">
          <div className="flex h-20 flex-col items-center justify-center text-center border-b border-slate-200 space-y-1">
            <p className="text-base font-semibold text-web-black-100">
              👋 在离开前，可以分享一下此刻的主要状态吗？
            </p>
            <p className="text-sm text-web-grey-600">
              您选择离开，主要是出于以下哪种情况？
            </p>
          </div>

          <div className="space-y-5 bg-white flex flex-col gap-3 overflow-hidden">
            {EXIT_SURVEY_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={selectedReasons.includes(option.value)}
                    onChange={() => toggleReason(option.value)}
                    color="primary"
                    className="p-0"
                    icon={
                      <RadioButtonUncheckedOutlinedIcon className="text-web-grey-50" />
                    }
                    checkedIcon={
                      <CheckCircleIcon className="!text-web-blue-500" />
                    }
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent',
                        '& .MuiSvgIcon-root': {
                          color: '#0050B3',
                        },
                      },
                    }}
                  />
                }
                label={
                  <span className="text-base leading-6">{option.label}</span>
                }
                labelPlacement="start"
                className="!m-0 flex h-11 py-1 border-b items-start"
                componentsProps={{
                  typography: { className: 'w-full text-left' },
                }}
              />
            ))}
          </div>

          {selectedReasons.includes('other') && (
            <TextField
              fullWidth
              multiline
              maxRows={4}
              minRows={4}
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="请填写您的反馈与想法"
              inputProps={{ maxLength: 500 }}
              helperText="字数限制500字以内"
              FormHelperTextProps={{
                className: '!text-xs !text-web-grey-600 !mt-2 !text-right',
              }}
              InputProps={{
                className: '!text-base !bg-[#F8F8FA]',
              }}
              sx={{
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  margin: 1.5,
                  padding: 0,
                },
                '& .MuiOutlinedInput-root': {
                  paddingBottom: 4,
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
              }}
            />
          )}
        </div>
      </UniversalModal>

      <UniversalModal
        open={thanksOpen}
        width={448}
        maxWidth={false}
        fullScreen={false}
        footer={<></>}
        data-ele-group={RESUME_EVALUATION_GROUP}
        data-ele-id="modal_感谢离开调查"
      >
        <div className="py-8 text-center bg-white rounded-xl bg-white">
          <div className="flex justify-center">
            <CheckCircleIcon
              className="!text-web-blue-500"
              sx={{ fontSize: 48 }}
            />
          </div>
          <p className="mt-4 font-bold text-xl leading-none">
            非常感谢您的反馈！
          </p>
          <p className="text-base mt-10 leading-none">
            页面将在 {thanksCountdown}s 后自动跳转
          </p>
        </div>
      </UniversalModal>
    </>
  );
};

export default ExitSurveyModal;
